import {
  Bell,
  Check,
  CheckSquare,
  Loader2,
  RotateCcw,
  Sparkles,
  Video,
  Wand2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import {
  acceptVoiceSuggestion,
  dismissVoiceSuggestion,
  listVoiceSuggestions,
  restoreVoiceSuggestion,
  type VoiceSuggestion,
} from '../../lib/voiceAssistantApi';

interface Props {
  conversationId: string;
}

const KIND_ICON = {
  TASK: CheckSquare,
  REMINDER: Bell,
  MEETING: Video,
  ACTION: Wand2,
} as const;

const HIDDEN_KEYS = new Set(['title', 'personal', 'id', '_before']);

/** Romanian labels for the arguments of a task edit. */
const FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  priority: 'Prioritate',
  assigneeName: 'Responsabil',
  dueDate: 'Termen',
  estimateHours: 'Estimare (h)',
  title: 'Titlu',
  description: 'Descriere',
  projectName: 'Proiect',
  labels: 'Etichete',
};

function fmt(v: unknown): string {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function detailLines(s: VoiceSuggestion): string[] {
  const p = s.payload;
  const line = (label: string, value: unknown): string | null =>
    typeof value === 'string' && value.trim() ? `${label}: ${value}` : null;
  const lines: (string | null)[] = [];
  if (s.kind === 'TASK') {
    lines.push(
      line('Proiect', p.projectName),
      line('Responsabil', p.assigneeName),
      line('Termen', p.dueDate),
      line('Prioritate', p.priority),
      typeof p.estimateHours === 'number' ? `Estimare: ${p.estimateHours} h` : null,
      line('Descriere', p.description),
    );
    if (p.personal === true) lines.push('Sarcină personală');
  } else if (s.kind === 'REMINDER') {
    lines.push(
      line('Când', typeof p.dueAt === 'string' ? p.dueAt.replace('T', ' ') : undefined),
      line('Descriere', p.description),
    );
  } else if (s.kind === 'ACTION') {
    // Generic: every argument as "Label: value"; for an edit that carries the
    // current values, "Label: before → after" so the change is obvious.
    const before = (p._before ?? {}) as Record<string, unknown>;
    for (const [key, value] of Object.entries(p)) {
      if (HIDDEN_KEYS.has(key) && key !== 'title') continue;
      if (key === 'title' && !(key in before)) continue;
      const label = FIELD_LABELS[key] ?? key;
      const isScalar =
        typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
      const isList = Array.isArray(value) && value.every((v) => typeof v === 'string');
      if (!isScalar && !isList) continue;
      lines.push(
        key in before ? `${label}: ${fmt(before[key])} → ${fmt(value)}` : `${label}: ${fmt(value)}`,
      );
    }
  } else {
    lines.push(
      line('Când', typeof p.scheduledAt === 'string' ? p.scheduledAt.replace('T', ' ') : undefined),
      typeof p.durationMinutes === 'number' ? `Durată: ${p.durationMinutes} min` : null,
      Array.isArray(p.invitees) && p.invitees.length ? `Invitați: ${p.invitees.join(', ')}` : null,
      line('Proiect', p.projectName),
      line('Client', p.clientName),
      line('Scop', p.notes),
    );
  }
  return lines.filter((l): l is string => Boolean(l));
}

function resultLink(s: VoiceSuggestion): { to: string; label: string; external?: boolean } | null {
  if (s.status !== 'ACCEPTED' || !s.resultId) return null;
  if (s.kind === 'TASK') {
    return {
      to: `/tasks/${encodeURIComponent(s.resultLabel ?? s.resultId)}`,
      label: s.resultLabel ?? 'task',
    };
  }
  if (s.kind === 'REMINDER') return { to: '/reminders', label: s.resultLabel ?? 'reminder' };
  if (s.kind === 'ACTION') {
    if (s.toolName === 'task_update' && s.resultLabel) {
      return { to: `/tasks/${encodeURIComponent(s.resultLabel)}`, label: s.resultLabel };
    }
    if (s.toolName?.startsWith('lead_')) return { to: '/leads', label: 'lead-uri' };
    if (s.toolName?.startsWith('client_')) return { to: '/clients', label: 'clienți' };
    if (s.toolName?.startsWith('kb_') && s.toolName.includes('project')) {
      return { to: '/projects', label: 'proiecte' };
    }
    return null;
  }
  return {
    to: s.resultLabel ?? `/meet`,
    label: 'Meet',
    external: Boolean(s.resultLabel?.startsWith('http')),
  };
}

/**
 * The actions Firmio Voice proposed during a conversation, for review: each
 * is a draft until accepted here (task / reminder / Meet meeting + invites).
 * Rendered at the top of the saved conversation in Firmio AI.
 */
export function VoiceSuggestionsPanel({ conversationId }: Props) {
  const { t } = useI18n();
  const [items, setItems] = useState<VoiceSuggestion[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showDecided, setShowDecided] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    void listVoiceSuggestions(conversationId)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  const run = useCallback(
    async (id: string, action: (id: string) => Promise<VoiceSuggestion>) => {
      setBusy(id);
      try {
        const updated = await action(id);
        setItems((list) => (list ? list.map((s) => (s.id === updated.id ? updated : s)) : list));
      } catch (err) {
        const message = err instanceof Error ? err.message : t('voice.suggestions.actionError');
        setItems((list) =>
          list ? list.map((s) => (s.id === id ? { ...s, error: message } : s)) : list,
        );
      } finally {
        setBusy(null);
      }
    },
    [t],
  );

  if (!items || items.length === 0) return null;

  const pending = items.filter((s) => s.status === 'PENDING');
  const decided = items.filter((s) => s.status !== 'PENDING');
  const visible = showDecided ? items : pending;
  if (!visible.length && !decided.length) return null;

  return (
    <div className="mx-4 mb-2 shrink-0 rounded-lg border border-primary/40 bg-card p-3 text-sm md:mx-0 md:mb-4">
      <div className="mb-2 flex items-center gap-2">
        <Sparkles size={16} className="text-primary" />
        <h2 className="font-heading text-sm font-semibold text-foreground">
          {t('voice.suggestions.title')}
        </h2>
        <span className="text-xs text-muted-foreground">
          {pending.length
            ? t('voice.suggestions.pendingCount', { count: pending.length })
            : t('voice.suggestions.allDecided')}
        </span>
        {decided.length > 0 && (
          <button
            type="button"
            onClick={() => setShowDecided((v) => !v)}
            className="ml-auto text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            {showDecided
              ? t('voice.suggestions.hideDecided')
              : t('voice.suggestions.showDecided', { count: decided.length })}
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{t('voice.suggestions.hint')}</p>
      <ul className="space-y-2">
        {visible.map((s) => {
          const Icon = KIND_ICON[s.kind];
          const link = resultLink(s);
          const isBusy = busy === s.id;
          const details = detailLines(s);
          return (
            <li
              key={s.id}
              className={`flex gap-3 rounded-md border p-3 ${
                s.status === 'ACCEPTED'
                  ? 'border-green-500/40 bg-green-500/5'
                  : s.status === 'DISMISSED'
                    ? 'border-border bg-muted/40 opacity-70'
                    : 'border-border bg-background'
              }`}
            >
              <Icon size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-foreground">{s.title}</div>
                {details.length > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">{details.join(' · ')}</div>
                )}
                {s.error && (
                  <div className="mt-1 text-xs text-destructive">
                    {t('voice.suggestions.failed')}: {s.error}
                  </div>
                )}
                {link && (
                  <div className="mt-1 text-xs">
                    {link.external ? (
                      <a
                        href={link.to}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {t('voice.suggestions.openMeeting')}
                      </a>
                    ) : (
                      <Link
                        to={link.to}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {t('voice.suggestions.open', { label: link.label })}
                      </Link>
                    )}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 items-start gap-1">
                {s.status === 'PENDING' && (
                  <>
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => void run(s.id, acceptVoiceSuggestion)}
                      className="flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                    >
                      {isBusy ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Check size={12} />
                      )}
                      {s.kind === 'MEETING'
                        ? t('voice.suggestions.acceptMeeting')
                        : s.kind === 'ACTION'
                          ? t('voice.suggestions.run')
                          : t('voice.suggestions.accept')}
                    </button>
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => void run(s.id, dismissVoiceSuggestion)}
                      title={t('voice.suggestions.dismiss')}
                      aria-label={t('voice.suggestions.dismiss')}
                      className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                    >
                      <X size={14} />
                    </button>
                  </>
                )}
                {s.status === 'DISMISSED' && (
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => void run(s.id, restoreVoiceSuggestion)}
                    className="flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                  >
                    <RotateCcw size={12} />
                    {t('voice.suggestions.restore')}
                  </button>
                )}
                {s.status === 'ACCEPTED' && (
                  <span className="flex items-center gap-1 text-xs text-green-600">
                    <Check size={12} /> {t('voice.suggestions.accepted')}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
